// frontend/components/Common/DateRangePicker.jsx
import React, { useState, useRef, useEffect } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function DateRangePicker({ value, onChange, presets = true }) {
    const [isOpen, setIsOpen] = useState(false);
    const [startDate, setStartDate] = useState(value?.startDate || new Date());
    const [endDate, setEndDate] = useState(value?.endDate || new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const pickerRef = useRef(null);

    const presetsList = [
        { label: 'Today', getDates: () => ({ start: new Date(), end: new Date() }) },
        { label: 'Yesterday', getDates: () => {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return { start: yesterday, end: yesterday };
        }},
        { label: 'This Week', getDates: () => {
            const start = new Date();
            start.setDate(start.getDate() - start.getDay());
            const end = new Date();
            end.setDate(end.getDate() + (6 - end.getDay()));
            return { start, end };
        }},
        { label: 'This Month', getDates: () => {
            const start = new Date();
            start.setDate(1);
            const end = new Date();
            end.setMonth(end.getMonth() + 1);
            end.setDate(0);
            return { start, end };
        }},
        { label: 'Last 30 Days', getDates: () => {
            const start = new Date();
            start.setDate(start.getDate() - 30);
            return { start, end: new Date() };
        }},
        { label: 'This Year', getDates: () => {
            const start = new Date();
            start.setMonth(0, 1);
            const end = new Date();
            end.setMonth(11, 31);
            return { start, end };
        }}
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleApply = () => {
        onChange({
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        });
        setIsOpen(false);
    };

    const handlePresetClick = (preset) => {
        const { start, end } = preset.getDates();
        setStartDate(start);
        setEndDate(end);
        onChange({
            startDate: start.toISOString().split('T')[0],
            endDate: end.toISOString().split('T')[0]
        });
        setIsOpen(false);
    };

    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="h-10" />);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const isSelected = date >= startDate && date <= endDate;
            const isStart = date.toDateString() === startDate.toDateString();
            const isEnd = date.toDateString() === endDate.toDateString();

            days.push(
                <button
                    key={day}
                    onClick={() => {
                        if (!startDate || (startDate && endDate)) {
                            setStartDate(date);
                            setEndDate(null);
                        } else if (startDate && !endDate) {
                            if (date < startDate) {
                                setStartDate(date);
                            } else {
                                setEndDate(date);
                            }
                        }
                    }}
                    className={`h-10 rounded-full transition-colors ${
                        isSelected ? 'bg-blue-100' : ''
                    } ${
                        isStart || isEnd ? 'bg-blue-600 text-white hover:bg-blue-700' : 'hover:bg-gray-100'
                    }`}
                >
                    {day}
                </button>
            );
        }

        return days;
    };

    return (
        <div className="relative" ref={pickerRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center space-x-2 px-4 py-2 border rounded-lg bg-white hover:bg-gray-50"
            >
                <CalendarIcon className="h-5 w-5 text-gray-500" />
                <span className="text-sm">
                    {value?.startDate && value?.endDate
                        ? `${new Date(value.startDate).toLocaleDateString()} - ${new Date(value.endDate).toLocaleDateString()}`
                        : 'Select date range'}
                </span>
            </button>

            {isOpen && (
                <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border z-50 p-4 w-auto min-w-[600px]">
                    <div className="flex">
                        {/* Calendar */}
                        <div className="flex-1">
                            <div className="flex justify-between items-center mb-4">
                                <button
                                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                                    className="p-1 hover:bg-gray-100 rounded"
                                >
                                    <ChevronLeftIcon className="h-5 w-5" />
                                </button>
                                <span className="font-medium">
                                    {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                                </span>
                                <button
                                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                                    className="p-1 hover:bg-gray-100 rounded"
                                >
                                    <ChevronRightIcon className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="grid grid-cols-7 gap-1 mb-2">
                                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                                    <div key={day} className="text-center text-xs font-medium text-gray-500">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {renderCalendar()}
                            </div>
                        </div>

                        {/* Presets */}
                        {presets && (
                            <div className="ml-4 pl-4 border-l">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Select</h4>
                                <div className="space-y-1">
                                    {presetsList.map(preset => (
                                        <button
                                            key={preset.label}
                                            onClick={() => handlePresetClick(preset)}
                                            className="block w-full text-left px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded"
                                        >
                                            {preset.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t flex justify-end space-x-2">
                        <button
                            onClick={() => setIsOpen(false)}
                            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}